import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, Edit, Trash2, AlertTriangle, Package, Upload, Download, ArrowLeftRight, AlertOctagon } from 'lucide-react';
import Swal from 'sweetalert2';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';

import API from '../api_config';

export default function InventoryPage() {
  const [inventory, setInventory] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Import State
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importWarehouse, setImportWarehouse] = useState('');
  const [importing, setImporting] = useState(false);

  // CRUD State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    product_id: '',
    warehouse_id: '',
    quantity: ''
  });

  // Transfer State
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [transferData, setTransferData] = useState({
      product_id: '',
      from_warehouse_id: '',
      to_warehouse_id: '',
      quantity: '',
      notes: ''
  });

  // Waste State
  const [isWasteOpen, setIsWasteOpen] = useState(false);
  const [wasteData, setWasteData] = useState({
      item_id: '',
      product_id: '',
      warehouse_id: '',
      product_name: '',
      quantity: '',
      notes: ''
  });

  const fetchInventory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/inventory`);
      setInventory(response.data);
    } catch (error) {
      toast.error('Error al cargar inventario');
    } finally {
      setLoading(false);
    }
  }, []);
  
  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/warehouses`);
      setWarehouses(response.data);
      if (response.data.length > 0) {
        setImportWarehouse(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
      try {
          const response = await axios.get(`${API}/products`);
          setProducts(response.data);
      } catch (error) {
          console.error("Error fetching products", error);
      }
  }, []);

  useEffect(() => {
    fetchInventory();
    fetchWarehouses();
    fetchProducts();
  }, [fetchInventory, fetchWarehouses, fetchProducts]);

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/inventory/import/template`, {
        responseType: 'blob', // Important for file handling
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_inventario.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Error al descargar la plantilla');
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile || !importWarehouse) {
      toast.error('Selecciona un archivo y una bodega');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', importFile);
    formData.append('warehouse_id', importWarehouse);

    try {
      const response = await axios.post(`${API}/inventory/import/excel`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`Advertencia: ${response.data.errors.length} filas fallaron`, {
            description: response.data.errors.slice(0, 3).join('\n') + (response.data.errors.length > 3 ? '...' : '')
        });
      }
      setIsImportOpen(false);
      setImportFile(null);
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar archivo');
    } finally {
      setImporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/inventory/${editingItem.id}`, {
            ...formData,
            quantity: parseFloat(formData.quantity),
            movement_type: 'adjustment',
            notes: 'Manual correction from UI'
        });
        toast.success('Inventario actualizado');
      } else {
        await axios.post(`${API}/inventory`, {
            ...formData,
            quantity: parseFloat(formData.quantity)
        });
        toast.success('Item de inventario creado');
      }
      setIsFormOpen(false);
      setEditingItem(null);
      fetchInventory();
    } catch (error) {
        console.error(error);
      toast.error('Error al guardar item de inventario');
    }
  };

  const handleTransfer = async (e) => {
      e.preventDefault();
      try {
          if (transferData.from_warehouse_id === transferData.to_warehouse_id) {
              toast.error('La bodega de origen y destino deben ser diferentes');
              return;
          }
          await axios.post(`${API}/inventory/transfer`, {
              ...transferData,
              quantity: parseFloat(transferData.quantity)
          });
          toast.success('Transferencia realizada con éxito');
          setIsTransferOpen(false);
          setTransferData({ product_id: '', from_warehouse_id: '', to_warehouse_id: '', quantity: '', notes: '' });
          fetchInventory();
      } catch (error) {
          toast.error(error.response?.data?.detail || 'Error al realizar transferencia');
      }
  };

  const handleWaste = async (e) => {
    e.preventDefault();
    try {
        await axios.post(`${API}/inventory/update`, {
            product_id: wasteData.product_id,
            warehouse_id: wasteData.warehouse_id,
            quantity: parseFloat(wasteData.quantity),
            movement_type: 'waste',
            notes: wasteData.notes || 'Merma reportada desde UI'
        });
        toast.success('Merma registrada correctamente');
        setIsWasteOpen(false);
        setWasteData({ item_id: '', product_id: '', warehouse_id: '', product_name: '', quantity: '', notes: '' });
        fetchInventory();
    } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al registrar merma');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: "Esto eliminará el registro de inventario",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API}/inventory/${id}`);
        Swal.fire('¡Eliminado!', 'El registro ha sido eliminado.', 'success');
        fetchInventory();
      } catch (error) {
        Swal.fire('Error', 'No se pudo eliminar el registro.', 'error');
      }
    }
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData({ product_id: '', warehouse_id: '', quantity: '' });
    setIsFormOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      product_id: item.product_id,
      warehouse_id: item.warehouse_id,
      quantity: item.quantity
    });
    setIsFormOpen(true);
  };

  const openWasteModal = (item) => {
      setWasteData({
          item_id: item.id,
          product_id: item.product_id,
          warehouse_id: item.warehouse_id,
          product_name: item.product?.name || 'Producto',
          quantity: '',
          notes: ''
      });
      setIsWasteOpen(true);
  };
    
  if (loading) {
    return <div className="flex items-center justify-center h-96">Cargando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Inventario</h1>
          <p className="text-muted-foreground mt-2">Control de stock por bodega</p>
        </div>
        
        <div className="flex space-x-2">
            <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
                <DialogTrigger asChild>
                    <Button onClick={openCreateModal} className="shadow-lg">
                        <Plus className="h-4 w-4 mr-2" />
                        Nuevo Item
                    </Button>
                </DialogTrigger>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingItem ? 'Editar Inventario' : 'Nuevo Item de Inventario'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Producto</Label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                value={formData.product_id}
                                onChange={(e) => setFormData({...formData, product_id: e.target.value})}
                                required
                                disabled={!!editingItem}
                            >
                                <option value="">Selecciona producto...</option>
                                {products.map(p => (
                                    <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <Label>Bodega</Label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                value={formData.warehouse_id}
                                onChange={(e) => setFormData({...formData, warehouse_id: e.target.value})}
                                required
                                disabled={!!editingItem}
                            >
                                <option value="">Selecciona bodega...</option>
                                {warehouses.map(w => (
                                    <option key={w.id} value={w.id}>{w.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <Label>Cantidad</Label>
                            <Input
                                type="number"
                                step="any"
                                value={formData.quantity}
                                onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                                required
                            />
                        </div>
                        <div className="flex justify-end space-x-2">
                            <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>Cancelar</Button>
                            <Button type="submit">{editingItem ? 'Actualizar' : 'Crear'}</Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={isTransferOpen} onOpenChange={setIsTransferOpen}>
                <DialogTrigger asChild>
                    <Button variant="outline" className="shadow-sm border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100">
                        <ArrowLeftRight className="h-4 w-4 mr-2" />
                        Transferir
                    </Button>
                </DialogTrigger>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Transferencia entre Bodegas</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleTransfer} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Producto</Label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                value={transferData.product_id}
                                onChange={(e) => setTransferData({...transferData, product_id: e.target.value})}
                                required
                            >
                                <option value="">Selecciona producto...</option>
                                {products.map(p => (
                                    <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                                ))}
                            </select>
                        </div>
                         <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Origen</Label>
                                <select
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={transferData.from_warehouse_id}
                                    onChange={(e) => setTransferData({...transferData, from_warehouse_id: e.target.value})}
                                    required
                                >
                                    <option value="">Bodega Origen...</option>
                                    {warehouses.map(w => (
                                        <option key={w.id} value={w.id}>{w.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <Label>Destino</Label>
                                <select
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={transferData.to_warehouse_id}
                                    onChange={(e) => setTransferData({...transferData, to_warehouse_id: e.target.value})}
                                    required
                                >
                                    <option value="">Bodega Destino...</option>
                                    {warehouses.filter(w => w.id !== transferData.from_warehouse_id).map(w => (
                                        <option key={w.id} value={w.id}>{w.name}</option>
                                    ))}
                                </select>
                            </div>
                         </div>
                        <div className="space-y-2">
                            <Label>Cantidad a Transferir</Label>
                            <Input
                                type="number"
                                step="any"
                                value={transferData.quantity}
                                onChange={(e) => setTransferData({...transferData, quantity: e.target.value})}
                                required
                                min="0.0001"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Notas (Opcional)</Label>
                            <Input
                                value={transferData.notes}
                                onChange={(e) => setTransferData({...transferData, notes: e.target.value})}
                                placeholder="Motivo de la transferencia..."
                            />
                        </div>
                        <div className="flex justify-end space-x-2">
                             <Button type="button" variant="outline" onClick={() => setIsTransferOpen(false)}>Cancelar</Button>
                             <Button type="submit">Realizar Transferencia</Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={isWasteOpen} onOpenChange={setIsWasteOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center text-red-600">
                            <AlertOctagon className="h-5 w-5 mr-2" />
                            Reportar Merma / Pérdida
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleWaste} className="space-y-4">
                        <div className="p-3 bg-red-50 text-red-800 rounded-md text-sm">
                            Estás reportando una salida de inventario para: <br />
                            <strong>{wasteData.product_name}</strong>
                        </div>
                        <div className="space-y-2">
                            <Label>Cantidad Perdida</Label>
                            <Input
                                type="number"
                                step="any"
                                value={wasteData.quantity}
                                onChange={(e) => setWasteData({...wasteData, quantity: e.target.value})}
                                required
                                min="0.0001"
                                placeholder="0.00"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Motivo / Notas</Label>
                            <Input
                                value={wasteData.notes}
                                onChange={(e) => setWasteData({...wasteData, notes: e.target.value})}
                                placeholder="Ej: Vencimiento, Daño, Robo..."
                                required
                            />
                        </div>
                        <div className="flex justify-end space-x-2">
                             <Button type="button" variant="outline" onClick={() => setIsWasteOpen(false)}>Cancelar</Button>
                             <Button type="submit" variant="destructive">Registrar Merma</Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
                <DialogTrigger asChild>
                    <Button variant="outline" className="shadow-sm">
                        <Upload className="h-4 w-4 mr-2" />
                        Importar Excel
                    </Button>
                </DialogTrigger>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Carga Masiva de Productos</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleImport} className="space-y-4">
                        <div className="bg-muted p-4 rounded-lg text-sm space-y-2">
                            <div className="flex justify-between items-start">
                                 <div>
                                    <p className="font-semibold">Instrucciones:</p>
                                    <p>Sube un archivo Excel (.xlsx) con las columnas requeridas.</p>
                                 </div>
                                 <Button type="button" variant="outline" size="sm" onClick={downloadTemplate}>
                                    <Download className="h-4 w-4 mr-2" />
                                    Descargar Plantilla
                                 </Button>
                            </div>
                            <ul className="list-disc list-inside font-mono text-xs mt-2">
                                <li>sku (Requerido)</li>
                                <li>barcode (Código de Barras)</li>
                                <li>name (Nombre del producto)</li>
                                <li>category (Categoría)</li>
                                <li>price (Precio de Venta)</li>
                                <li>cost (Costo Unitario)</li>
                                <li>quantity (Cantidad a cargar)</li>
                                <li>min_stock (Stock Mínimo - opcional)</li>
                            </ul>
                        </div>
    
                        <div className="space-y-2">
                            <Label>Bodega Destino</Label>
                            <select 
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                value={importWarehouse}
                                onChange={(e) => setImportWarehouse(e.target.value)}
                                required
                            >
                                <option value="">Selecciona una bodega...</option>
                                {warehouses.map(wh => (
                                    <option key={wh.id} value={wh.id}>{wh.name}</option>
                                ))}
                            </select>
                        </div>
    
                        <div className="space-y-2">
                            <Label>Archivo Excel</Label>
                            <Input 
                                type="file" 
                                accept=".xlsx, .xls"
                                onChange={(e) => setImportFile(e.target.files[0])}
                                required
                            />
                        </div>
    
                        <div className="flex justify-end pt-4">
                            <Button type="submit" disabled={importing}>
                                {importing ? 'Procesando...' : 'Cargar Inventario'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {inventory.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay inventario registrado</p>
            </CardContent>
          </Card>
        ) : (
            <div className="rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                    <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Producto</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">SKU</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Categoría</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Bodega</th>
                    <th className="h-12 px-4 text-right align-middle font-medium text-muted-foreground">Cantidad</th>
                    <th className="h-12 px-4 text-center align-middle font-medium text-muted-foreground">Unidad</th>
                    <th className="h-12 px-4 text-right align-middle font-medium text-muted-foreground">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {inventory.map((item) => {
                    const isLowStock = item.product && item.quantity < item.product.min_stock;
                     return (
                      <tr key={item.id} className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                        <td className="p-4 align-middle font-medium">
                          {item.product?.name || 'Producto desconocido'}
                          {isLowStock && (
                            <div className="inline-flex items-center ml-2 px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                              <AlertTriangle className="mr-1 h-3 w-3" />
                              Bajo Stock
                            </div>
                          )}
                        </td>
                        <td className="p-4 align-middle">{item.product?.sku || '-'}</td>
                        <td className="p-4 align-middle">{item.product?.category || '-'}</td>
                        <td className="p-4 align-middle">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {item.warehouse?.name || 'Sin Bodega'}
                          </span>
                        </td>
                        <td className="p-4 align-middle text-right font-bold">{item.quantity}</td>
                        <td className="p-4 align-middle text-center text-muted-foreground">{item.product?.unit || 'un'}</td>
                        <td className="p-4 align-middle text-right">
                          <div className="flex justify-end space-x-2">
                             <Button variant="ghost" size="sm" onClick={() => openEditModal(item)}>
                               <Edit className="h-4 w-4" />
                             </Button>
                             <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => openWasteModal(item)}
                                title="Reportar Merma"
                            >
                                <AlertOctagon className="h-4 w-4 text-orange-500" />
                             </Button>
                             <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)}>
                               <Trash2 className="h-4 w-4 text-red-500" />
                             </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
        )}
      </div>
    </div>
  );
}